import { useUserSession } from '../../../../models/hooks/appStateHooks';
import type { CallMix, ICoachLabPersona } from './labLogic';

const DAY_MS = 24 * 60 * 60 * 1000;
const PLAN_TTL_MS = 14 * DAY_MS;
const PERSONA_TTL_MS = 30 * DAY_MS;

interface IEntry<T> {
	value: T;
	savedAt: number;
}

/** Per-browser Coach Lab memory, keyed by user so a shared machine never hands one manager another's leads. */
export const coachLabCache = (userId: string) => {
	const prefix = `coachLab:${userId}:`;

	const read = <T>(key: string, ttlMs?: number): T | null => {
		try {
			const raw = window.localStorage.getItem(prefix + key);
			if (!raw) return null;
			const entry = JSON.parse(raw) as IEntry<T>;
			return ttlMs && Date.now() - entry.savedAt > ttlMs ? null : entry.value;
		} catch {
			return null;
		}
	};

	const write = (key: string, value: unknown) => {
		try {
			window.localStorage.setItem(prefix + key, JSON.stringify({ savedAt: Date.now(), value }));
		} catch {
			// Storage full or blocked: the call still works, it just regenerates next time.
		}
	};

	const calledLeads = () => read<string[]>('called') ?? [];

	return {
		cachedPersona: (companyId: string) => read<ICoachLabPersona>(`persona:${companyId}`, PERSONA_TTL_MS),
		cachedPlan: (leadKey: string) => read<string>(`plan:${leadKey}`, PLAN_TTL_MS),
		calledLeads,
		callMix: () => read<CallMix>('callMix') ?? 'real',
		lastCoachId: () => read<string>('lastCoach'),
		markCalled: (companyId: string) => write('called', Array.from(new Set([...calledLeads(), companyId]))),
		saveCallMix: (mix: CallMix) => write('callMix', mix),
		saveLastCoachId: (id: string) => write('lastCoach', id),
		savePersona: (companyId: string, persona: ICoachLabPersona) => write(`persona:${companyId}`, persona),
		savePlan: (leadKey: string, plan: string) => write(`plan:${leadKey}`, plan),
	};
};

const noCache: ReturnType<typeof coachLabCache> = {
	cachedPersona: () => null,
	cachedPlan: () => null,
	callMix: () => 'real',
	calledLeads: () => [],
	lastCoachId: () => null,
	markCalled: () => undefined,
	saveCallMix: () => undefined,
	saveLastCoachId: () => undefined,
	savePersona: () => undefined,
	savePlan: () => undefined,
};

export const useCoachLabCache = () => {
	const userId = useUserSession().user?.id;
	return userId ? coachLabCache(userId) : noCache;
};
